'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  AtaliaConversation,
  AtaliaEnvelope,
  AtaliaMessage,
} from '@/lib/atalia/types'
import { cn } from '@/lib/utils'

const MESSAGES_PAGE_SIZE = 100

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

interface MessagesPage {
  messages: AtaliaMessage[]
  page: number
  total: number
  limit: number
}

async function fetchMessages(
  conversationId: number,
  page: number
): Promise<MessagesPage> {
  const response = await fetch(
    `/api/atalia/conversations/${conversationId}/messages?page=${page}&limit=${MESSAGES_PAGE_SIZE}`
  )
  const body = (await response.json()) as AtaliaEnvelope<AtaliaMessage[]>
  if (!(response.ok && body.success)) {
    throw new Error(body.error ?? 'Erro ao buscar mensagens')
  }

  const messages = body.data ?? []
  return {
    messages,
    page: body.meta?.page ?? page,
    limit: body.meta?.limit ?? MESSAGES_PAGE_SIZE,
    total: body.meta?.total ?? messages.length,
  }
}

function MessageBubble({ message }: { message: AtaliaMessage }) {
  // `in` = enviada pelo aluno; `out` = equipe/bot.
  const isFromStudent = message.direction === 'in'

  return (
    <li
      className={cn(
        'flex flex-col gap-1',
        isFromStudent ? 'items-end' : 'items-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
          isFromStudent
            ? 'rounded-br-sm bg-primary-500 text-primary-50'
            : 'rounded-bl-sm bg-primary-50 text-[#333333]'
        )}
      >
        {message.content ? <p>{message.content}</p> : null}
        {message.media_url ? (
          <a
            className="mt-1 inline-flex items-center gap-1 underline underline-offset-2"
            href={message.media_url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Paperclip aria-hidden="true" className="size-3.5" />
            Ver anexo
          </a>
        ) : null}
        {message.content || message.media_url ? null : (
          <p className="italic opacity-80">Mensagem sem conteúdo de texto</p>
        )}
      </div>
      <time className="text-[#656566] text-[11px]" dateTime={message.created_at}>
        {isFromStudent ? 'Você' : 'Suporte'} ·{' '}
        {timeFormatter.format(new Date(message.created_at))}
      </time>
    </li>
  )
}

function TranscriptBody({ conversationId }: { conversationId: number }) {
  const {
    data,
    error,
    isPending,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['atalia', 'conversations', conversationId, 'messages'],
    queryFn: ({ pageParam }) => fetchMessages(conversationId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.limit < lastPage.total
        ? lastPage.page + 1
        : undefined,
    retry: false,
  })

  if (isPending) {
    return (
      <div className="flex flex-col gap-3" role="status">
        <p className="flex items-center justify-center gap-2 py-2 text-[#656566] text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          Carregando mensagens...
        </p>
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="ml-auto h-10 w-1/2" />
        <Skeleton className="h-14 w-3/4" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <p className="text-[#656566] text-sm">
          Não foi possível carregar as mensagens deste atendimento.
        </p>
        <Button onClick={() => refetch()} type="button" variant="outline">
          Tentar novamente
        </Button>
      </div>
    )
  }

  const messages = data.pages.flatMap((page) => page.messages)

  if (messages.length === 0) {
    return (
      <p className="py-6 text-center text-[#656566] text-sm">
        Este atendimento não possui mensagens.
      </p>
    )
  }

  return (
    <div className="flex max-h-96 flex-col gap-3 overflow-y-auto pr-1">
      <ul className="flex flex-col gap-3">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </ul>
      {hasNextPage ? (
        <Button
          className="self-center"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          type="button"
          variant="outline"
        >
          {isFetchingNextPage ? 'Carregando...' : 'Carregar mais mensagens'}
        </Button>
      ) : null}
    </div>
  )
}

interface AttendanceTranscriptProps {
  conversation: AtaliaConversation
  statusLabel: string
  dateLabel: string
  onBack: () => void
}

export function AttendanceTranscript({
  conversation,
  statusLabel,
  dateLabel,
  onBack,
}: AttendanceTranscriptProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 border-b pb-3">
        <button
          className="inline-flex items-center gap-1 font-medium text-[#41196B] text-sm"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Voltar
        </button>
        <div className="text-right">
          <p className="font-medium text-[#41196B] text-sm">
            Atendimento #{conversation.id}
          </p>
          <p className="text-[#656566] text-xs">
            {dateLabel} · {statusLabel}
          </p>
        </div>
      </div>

      <TranscriptBody conversationId={conversation.id} />
    </div>
  )
}
